import { Component, OnInit, ViewChild } from '@angular/core';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MetricsService } from '../../shared/metrics.service';
import { CpSpeedAnomalies } from '@app/shared/models/metrics/cp-speed-anomalies.model';
import { CpTopDownloads } from '@app/shared/models/metrics/cp-top-downloads.model';
import { CpTopUploads } from '@app/shared/models/metrics/cp-top-uploads.model';

@Component({
    selector: 'app-cp-speed-tests',
    templateUrl: './cp-speed-tests.component.html',
    styleUrls: ['./cp-speed-tests.component.scss'],
    standalone: false
})
export class CpSpeedTestsComponent implements OnInit {
  
  @ViewChild('speedTestAnomaliesTable') speedTestAnomaliesTable: MatTable<CpSpeedAnomalies>;
  @ViewChild('anomalyPaginator', { static: true }) paginatorA: MatPaginator;

  @ViewChild('bestDownloadsTable') bestDownloadsTable: MatTable<CpTopDownloads>;
  @ViewChild('bdPaginator', { static: true }) paginatorBD: MatPaginator;

  @ViewChild('bestUploadsTable') bestUploadsTable: MatTable<CpTopUploads>;
  @ViewChild('buPaginator', { static: true }) paginatorBU: MatPaginator;

  @ViewChild('worstDownloadsTable') worstDownloadsTable: MatTable<CpTopDownloads>;
  @ViewChild('wdPaginator', { static: true }) paginatorWD: MatPaginator;

  @ViewChild('worstUploadsTable') worstUploadsTable: MatTable<CpTopUploads>;
  @ViewChild('wuPaginator', { static: true }) paginatorWU: MatPaginator;

  isLoading: boolean;
  displayedColumnsAnomalies = ['store', 'hostname', 'download', 'pctle_dl', 'upload', 'pctle_ul', 'test_date'];
  displayedColumnsBestDownloads = ['store', 'hostname', 'avg_download', 'carrier', 'net_device_id'];
  displayedColumnsBestUploads = ['store', 'hostname', 'avg_upload', 'carrier', 'net_device_id'];
  displayedColumnsWorstDownloads = ['store', 'hostname', 'avg_download', 'carrier', 'net_device_id'];
  displayedColumnsWorstUploads = ['store', 'hostname', 'avg_upload', 'carrier', 'net_device_id'];
  anomalies = [];
  bestDownloads = [];
  bestUploads = [];
  worstDownloads = [];
  worstUploads = [];
  anomaly_pctle = 'N';
  flag;
  isOnDownloadsTab = true;

  speedTestAnomaliesDataSource = new MatTableDataSource<CpSpeedAnomalies>(this.anomalies);
  bestDownloadsDataSource = new MatTableDataSource<CpTopDownloads>(this.bestDownloads);
  bestUploadsDataSource = new MatTableDataSource<CpTopUploads>(this.bestUploads);
  worstDownloadsDataSource = new MatTableDataSource<CpTopDownloads>(this.worstDownloads);
  worstUploadsDataSource = new MatTableDataSource<CpTopUploads>(this.worstUploads);

  
  constructor( private metricsService: MetricsService) { }
  
  ngOnInit() {
    this.isLoading = true;
    this.flag = "download";

    Promise.all(
      [
        this.getAnomalies(),
        this.getBestDownloads(),
        this.getBestUploads(),
        this.getWorstDownloads(),
        this.getWorstUploads()
      ]
    ).then(() => {
      this.isLoading = false;
    })   
    .catch(() => {
      console.log('Promise.all REJECTED');
      this.isLoading = false;
    })

  }
  
  applyAnomaliesFilter(filterValue: string) {
    this.speedTestAnomaliesDataSource.filter = filterValue.trim().toLowerCase();
  }

  getPctleColorCoding(actualValue: string, pctleValue: string) {
    if (parseFloat(actualValue) < parseFloat(pctleValue)) {
      return 'red'
    } else {
      return 'black'
    }
  }

  changeTableViewToDownloads() {
    this.flag = 'download';
    this.isOnDownloadsTab = true;
  }

  changeTableViewToUploads() {
    this.flag = 'upload';
    this.isOnDownloadsTab = false;
  }

  getAnomalies() {
    const promise = new Promise((resolve, reject) => {
      this.metricsService.getCradlepointSpeedAnomalies().then(response => {
        response.toPromise().then(response => {
          try {
            response["message"].forEach(test => {
              this.anomalies.push(test);
            });
            this.speedTestAnomaliesDataSource.paginator = this.paginatorA;
            this.speedTestAnomaliesTable.renderRows();
            this.anomaly_pctle = response["pctle"]
          } catch (error) {
            console.log("CP anomalies timed out!");
          } finally {
            resolve(true);
          }
        });
      });
    });

    return promise;
  }

  getBestDownloads() {
    const promise = new Promise((resolve, reject) => {
      this.metricsService.getCradlepointBestDownloads().then(response => {
        response.toPromise().then(response => {
          try {
            response["message"].forEach(stat => {
              this.bestDownloads.push(stat);
            });
            this.bestDownloadsDataSource.paginator = this.paginatorBD;
            this.bestDownloadsTable.renderRows();
          } catch (error) {
            console.log("CP best downloads timed out!");
          } finally {
            resolve(true);
          }
        });
      });
    });

    return promise;
  }

  getBestUploads() {
    const promise = new Promise((resolve, reject) => {
      this.metricsService.getCradlepointBestUploads().then(response => {
        response.toPromise().then(response => {
          try {
            response["message"].forEach(stat => {
              this.bestUploads.push(stat);
            });
            this.bestUploadsDataSource.paginator = this.paginatorBU;
            this.bestUploadsTable.renderRows();
          } catch (error) {
            console.log("CP best uploads timed out!");
          } finally {
            resolve(true);
          }
        });
      });
    });

    return promise;
  }

  getWorstDownloads() {
    const promise = new Promise((resolve, reject) => {
      this.metricsService.getCradlepointWorstDownloads().then(response => {
        response.toPromise().then(response => {
          try {
            response["message"].forEach(stat => {
              this.worstDownloads.push(stat);
            });
            this.worstDownloadsDataSource.paginator = this.paginatorWD;
            this.worstDownloadsTable.renderRows();
          } catch (error) {
            console.log("CP worst downloads timed out!");
          } finally {
            resolve(true);
          }
        });
      });
    });

    return promise;
  }

  getWorstUploads() {
    const promise = new Promise((resolve, reject) => {
      this.metricsService.getCradlepointWorstUploads().then(response => {
        response.toPromise().then(response => {
          try {
            response["message"].forEach(stat => {
              this.worstUploads.push(stat);
            });
            this.worstUploadsDataSource.paginator = this.paginatorWU;
            this.worstUploadsTable.renderRows();
          } catch (error) {
            console.log("CP worst uploads timed out!");
          } finally {
            resolve(true);
          }
        });
      });
    });

    return promise;
  }

}
